import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import CloudDoneRoundedIcon from '@mui/icons-material/CloudDoneRounded'
import CloudOffRoundedIcon from '@mui/icons-material/CloudOffRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import SyncRoundedIcon from '@mui/icons-material/SyncRounded'
import { Button } from '../Common/Button'
import { Pressable } from '../Common/Pressable'
import { EmptyHubState } from '../Common/EmptyHubState'
import { HelperEmptyState } from '../Helper/HelperEmptyState'
import { HeroHeaderCard } from '../Common/HeroHeaderCard'
import { BottomSheet } from '../Common/BottomSheet'
import { useNls } from '../../nls/nlsProvider'
import {
  getHelperSnapshot,
  subscribeHelperStore,
  type HelperStoreSnapshot,
} from '../../services/helper/helperStore'
import { refreshHelperQueue, removeMutation } from '../../services/helper/queueService'
import { drainOutbox, retryMutation } from '../../services/helper/syncWorker'
import {
  buildMobileSyncSummary,
  bucketPendingByKind,
  getMobileFailureKind,
  getMobileFailureMessage,
  getMobileOperationState,
  getMobileOperationStateLabel,
  getMobileOperationTargetId,
  getMobileOperationTitle,
  type MobileSyncRole,
  type MobileSyncTone,
} from '../../services/mobileSync/mobileSyncSummary'
import {
  clearHelperOutboxForWorkspace,
  type HelperMutationRecord,
  type HelperMutationType,
} from '../../services/helper/helperOutboxDb'
import './mobileSync.css'

type Props = {
  role: MobileSyncRole
  onOpenItem?: (itemId: string) => void
  compact?: boolean
}

type StatusPillProps = {
  role: MobileSyncRole
  onOpen: () => void
  className?: string
}

const TONE_ICON: Record<MobileSyncTone, ReactNode> = {
  healthy: <CloudDoneRoundedIcon fontSize="small" />,
  working: <SyncRoundedIcon fontSize="small" />,
  offline: <CloudOffRoundedIcon fontSize="small" />,
  attention: <ErrorOutlineRoundedIcon fontSize="small" />,
}

const roleNounKey = (role: MobileSyncRole): 'NLSMobileSyncRoleHelper' | 'NLSMobileSyncRoleMember' =>
  role === 'HELPER' ? 'NLSMobileSyncRoleHelper' : 'NLSMobileSyncRoleMember'

const useMobileSyncSnapshot = () => {
  const [snapshot, setSnapshot] = useState<HelperStoreSnapshot>(() => getHelperSnapshot())
  const [online, setOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine)

  useEffect(() => {
    const unsubscribe = subscribeHelperStore(setSnapshot)
    void refreshHelperQueue()
    const handleOnline = () => setOnline(true)
    const handleOffline = () => setOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      unsubscribe()
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return { snapshot, online }
}

const OperationRow = ({
  record,
  snapshot,
  onOpenItem,
}: {
  record: HelperMutationRecord & { completedAt?: number }
  snapshot: HelperStoreSnapshot
  onOpenItem?: (itemId: string) => void
}) => {
  const { t } = useNls()
  const state = getMobileOperationState(record, snapshot.operationPhases[record.id])
  const targetId = getMobileOperationTargetId(record)
  const failureKind = getMobileFailureKind(record.error)
  const isAccessLost = record.status === 'ACCESS_LOST'
  const isFailed = state === 'FAILED' || isAccessLost
  const canRetry = !isAccessLost && (isFailed || record.status === 'PENDING')
  const canDiscard = record.status === 'FAILED' || record.status === 'PENDING' || isAccessLost

  return (
    <article className={`mobileSyncOperation mobileSyncOperation--${state.toLowerCase()}`}>
      <div className="mobileSyncOperation__icon" aria-hidden="true">
        {record.type === 'CHECKLIST_PROOF_UPLOAD' || record.type === 'CHECKLIST_VOICE_MEMO'
          ? <UploadFileRoundedIcon fontSize="small" />
          : state === 'FAILED'
            ? <ErrorOutlineRoundedIcon fontSize="small" />
            : <SyncRoundedIcon fontSize="small" />}
      </div>
      <div className="mobileSyncOperation__body">
        <div className="mobileSyncOperation__topline">
          <strong>{getMobileOperationTitle(record)}</strong>
          <span>{getMobileOperationStateLabel(state)}</span>
        </div>
        {isFailed ? (
          <p>{getMobileFailureMessage(record.error)}</p>
        ) : state === 'SYNCED' && record.completedAt ? (
          <p>{t('NLSMobileSyncSyncedAtTime', { time: new Date(record.completedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) })}</p>
        ) : (
          <p>{state === 'QUEUED' ? t('NLSMobileSyncHeldSafely') : t('NLSMobileSyncSending')}</p>
        )}
        <div className="mobileSyncOperation__actions">
          {canRetry ? (
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              icon={<RefreshRoundedIcon fontSize="small" />}
              onClick={() => void retryMutation(record.id)}
            >
              {t('NLSMobileSyncRetry')}
            </Button>
          ) : null}
          {canDiscard ? (
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              tone="danger"
              icon={<DeleteOutlineRoundedIcon fontSize="small" />}
              onClick={() => void removeMutation(record.id)}
            >
              {failureKind === 'CONFLICT' ? t('NLSMobileSyncUseLatest') : t('NLSMobileSyncDiscard')}
            </Button>
          ) : null}
          {targetId && onOpenItem ? (
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              icon={<OpenInNewRoundedIcon fontSize="small" />}
              onClick={() => onOpenItem(targetId)}
            >
              {t('NLSMobileSyncOpenTask')}
            </Button>
          ) : null}
        </div>
      </div>
    </article>
  )
}

export const MobileSyncStatusPill = ({ role, onOpen, className = '' }: StatusPillProps) => {
  const { t } = useNls()
  const { snapshot, online } = useMobileSyncSnapshot()
  const summary = useMemo(() => buildMobileSyncSummary({
    syncState: snapshot.syncState,
    pendingCount: snapshot.pending.length,
    failedCount: snapshot.failed.length,
    completedCount: snapshot.recentCompleted.length,
    lastSyncAt: snapshot.lastSyncAt,
    isOnline: online,
  }), [online, snapshot.failed.length, snapshot.lastSyncAt, snapshot.pending.length, snapshot.recentCompleted.length, snapshot.syncState])

  const count = snapshot.failed.length || snapshot.pending.length
  const showLabel = count > 0 || summary.headline !== 'Ready to work'
  return (
    <Pressable
      type="button"
      className={`mobileSyncPill mobileSyncPill--${summary.tone} ${className}`}
      onClick={onOpen}
      // §8.6.6 — the label is the ACTION, the description carries the STATUS.
      // Screen readers used to read the whole status ("Open sync center: Ready
      // to work") on every focus; splitting keeps the action terse and lets
      // aria-live regions carry status updates without re-announcing "Open
      // sync center" every time.
      aria-label={t('NLSMobileSyncPillOpen')}
      aria-describedby={`sync-status-${role.toLowerCase()}`}
      data-testid={`${role.toLowerCase()}-sync-pill`}
    >
      {TONE_ICON[summary.tone]}
      {showLabel ? <span>{count > 0 ? t('NLSMobileSyncPillPendingCount', { count }) : summary.headline}</span> : null}
      <span id={`sync-status-${role.toLowerCase()}`} className="sr-only">
        {summary.headline}
      </span>
    </Pressable>
  )
}

export const MobileSyncCenter = ({ role, onOpenItem, compact = false }: Props) => {
  const { t } = useNls()
  const isMemberChrome = role === 'MEMBER'
  const { snapshot, online } = useMobileSyncSnapshot()
  const summary = useMemo(() => buildMobileSyncSummary({
    syncState: snapshot.syncState,
    pendingCount: snapshot.pending.length,
    failedCount: snapshot.failed.length,
    completedCount: snapshot.recentCompleted.length,
    lastSyncAt: snapshot.lastSyncAt,
    isOnline: online,
  }), [online, snapshot.failed.length, snapshot.lastSyncAt, snapshot.pending.length, snapshot.recentCompleted.length, snapshot.syncState])

  const operations = useMemo(() => [
    ...snapshot.failed,
    ...snapshot.pending,
    ...snapshot.recentCompleted,
  ], [snapshot.failed, snapshot.pending, snapshot.recentCompleted])
  const kindBreakdown = useMemo(() => {
    const kindLabels: Record<HelperMutationType, string> = {
      CHECKLIST_PROOF_UPLOAD: t('NLSMobileSyncKindProofUpload'),
      CHECKLIST_TOGGLE:       t('NLSMobileSyncKindStatusUpdate'),
      CHECKLIST_COMMENT:      t('NLSMobileSyncKindComment'),
      CHECKLIST_VOICE_MEMO:   t('NLSMobileSyncKindVoiceMemo'),
      PENDING_EXPENSE_INTAKE: t('NLSMobileSyncKindExpenseIntake'),
      STOCK_COUNT_ROUND:      t('NLSMobileSyncKindStockCount'),
      STOCK_SCAN_SUGGESTION:  t('NLSMobileSyncKindStockScan'),
      WORKSPACE_ACTIVITY:     t('NLSMobileSyncKindWorkspaceActivity'),
      EDU_BOARDING:           t('NLSMobileSyncKindEduBoarding'),
      EDU_ATTENDANCE_MARK:    t('NLSMobileSyncKindEduAttendanceMark'),
      TIME_CLOCK:             t('NLSMobileSyncKindTimeClock'),
      OPS_DUE_RECORD:         t('NLSMobileSyncKindDueRecord'),
      OPS_CHECKPOINT_SCAN:    t('NLSMobileSyncKindCheckpointScan'),
      OPS_SKIP_DUE:           t('NLSMobileSyncKindSkipDue'),
      OPS_RAISE_REQUEST:      t('NLSMobileSyncKindRaiseRequest'),
      OPS_ATTENDANCE_ROLL:    t('NLSMobileSyncKindAttendanceRoll'),
      OPS_START_REQUEST:      t('NLSHelperOpsStartRequestBtn'),
      OPS_MARK_REQUEST_DONE:  t('NLSHelperOpsDoneRequestBtn'),
      OPS_BMW_SAVE:           t('NLSOpsHosBmwDailyTitle'),
      OPS_DPR_SAVE:           t('NLSHelperOpsDprTitle'),
      OPS_CON_ISSUE_CREATE:   t('NLSHelperOpsHindranceSectionTitle'),
      OPS_CON_ISSUE_ATTACH_PHOTOS: t('NLSMobileSyncKindConIssueAttachPhotos'),
    }
    return bucketPendingByKind(snapshot.pending, kindLabels)
  }, [snapshot.pending, t])

  const [selectedKind, setSelectedKind] = useState<HelperMutationType | null>(null)

  const selectedKindLabel = useMemo(() => {
    if (!selectedKind) return ''
    return kindBreakdown.find((item) => item.type === selectedKind)?.label ?? selectedKind
  }, [kindBreakdown, selectedKind])

  const filteredOperations = useMemo(() => {
    if (!selectedKind) return []
    return operations.filter((op) => op.type === selectedKind)
  }, [operations, selectedKind])

  const accessLostWorkspaces = useMemo(() => {
    const wsIds = new Set<string>()
    operations.forEach((op) => {
      if (op.status === 'ACCESS_LOST') {
        const wsId = (op.payload as { workspaceId?: string })?.workspaceId
        if (wsId) wsIds.add(wsId)
      }
    })
    return Array.from(wsIds)
  }, [operations])

  const handleDiscardAllAccessLost = async () => {
    for (const wsId of accessLostWorkspaces) {
      await clearHelperOutboxForWorkspace(wsId)
    }
    await refreshHelperQueue()
  }

  const syncButtonLabel = snapshot.syncState === 'SYNCING' ? t('NLSMobileSyncSyncing') : t('NLSMobileSyncSyncNow')

  return (
    <section className={`mobileSyncCenter ${compact ? 'mobileSyncCenter--compact' : ''}`} data-testid={`${role.toLowerCase()}-sync-center`}>
      {isMemberChrome ? <HeroHeaderCard eyebrow={t('NLSMobileSyncMemberHeroEyebrow')} title={summary.headline} className={`mobileSyncHero mobileSyncHero--${summary.tone}`} rightSlot={<div className="mobileSyncMemberActions"><span>{summary.detail}</span><Button type="button" variant="secondary" size="sm" onClick={() => void drainOutbox()} disabled={!online || snapshot.syncState === 'SYNCING'}>{syncButtonLabel}</Button></div>} /> : <header className={`relative mobileSyncHero mobileSyncHero--${summary.tone}`}>
        <span className="mobileSyncHero__icon" aria-hidden="true">{TONE_ICON[summary.tone]}</span>
        <div>
          <p>{t('NLSMobileSyncHelperHeroEyebrow')}</p>
          <h2>{summary.headline}</h2>
          <span>{summary.detail}</span>
          <p className="mobileSyncHero__subtitle text-[11px] font-medium text-[var(--muted)] mt-1">{t('NLSMobileSyncFullCenterSubtitle')}</p>
        </div>
        <Button type="button" variant="secondary" size="sm" className="absolute bottom-3 right-3" onClick={() => void drainOutbox()} disabled={!online || snapshot.syncState === 'SYNCING'} icon={<RefreshRoundedIcon fontSize="small" />}>{syncButtonLabel}</Button>
      </header>}

      <div className="mobileSyncGuide">
        {isMemberChrome ? <span className="mobileSyncGuide__eyebrow">{t('NLSMobileSyncMemberOfflinePolicy')}</span> : null}
        <strong>{t('NLSMobileSyncSafeOfflineTitle')}</strong>
        <span>{t('NLSMobileSyncSafeOfflineBody')}</span>
      </div>
      {!isMemberChrome && snapshot.pending.length > 0 ? (
        <div className="mobileSyncBreakdown rounded-2xl border border-[var(--border)] bg-[var(--surface-muted)] p-3" role="table" aria-label={t('NLSMobileSyncBreakdownAria')}>
          {kindBreakdown.map(({ type, label, count }) => (
            <Pressable
              key={type}
              role="row"
              onClick={() => setSelectedKind(type)}
              className={`flex w-full items-center justify-between py-1.5 text-left text-xs font-semibold transition hover:opacity-80 ${count === 0 ? 'text-[var(--muted)]' : 'text-[var(--text-strong)]'}`}
              data-testid={`mobile-sync-breakdown-${type.toLowerCase()}`}
            >
              <span role="cell">{label}</span>
              <span role="cell" className={count === 0 ? 'text-[var(--muted)]' : 'text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))]'}>
                {count} {t('NLSMobileSyncBreakdownPendingSuffix')}
              </span>
            </Pressable>
          ))}
        </div>
      ) : null}

      {accessLostWorkspaces.length > 0 ? (
        <div className="flex items-center justify-between p-3 rounded-2xl border border-[var(--border)] bg-[var(--surface-muted)]" data-testid="mobile-sync-access-lost-banner">
          <span className="text-xs text-[var(--muted)]">{t('NLSMutationAccessLost', { name: 'this workspace' })}</span>
          <Button
            type="button"
            variant="secondary"
            tone="danger"
            size="sm"
            onClick={() => void handleDiscardAllAccessLost()}
            data-testid="mobile-sync-discard-all"
          >
            {t('NLSMobileSyncDiscardAll')}
          </Button>
        </div>
      ) : null}

      {operations.length === 0 ? (isMemberChrome ? <EmptyHubState preset="syncNothingWaiting" appearance="card" className="mobileSyncEmpty" /> : (
        <HelperEmptyState icon={<CloudDoneRoundedIcon />} title={t('NLSMobileSyncEmptyTitle')} body={t('NLSMobileSyncEmptyBody', { role: t(roleNounKey(role)) })} />
      )) : (
        <div className="mobileSyncList" aria-label={t('NLSMobileSyncOperationsAria')}>
          {operations.map((record) => (
            <OperationRow
              key={`${record.id}-${record.status}`}
              record={record}
              snapshot={snapshot}
              onOpenItem={onOpenItem}
            />
          ))}
        </div>
      )}

      <BottomSheet
        isOpen={Boolean(selectedKind)}
        onClose={() => setSelectedKind(null)}
        title={selectedKindLabel}
        className="mobileSyncFilteredSheet"
      >
        <div className="mobileSyncList p-3" aria-label={selectedKindLabel} data-testid="mobile-sync-filtered-list">
          {filteredOperations.length === 0 ? (
            <p className="py-4 text-center text-xs text-[var(--muted)]">
              {t('NLSMobileSyncEmptyTitle')}
            </p>
          ) : (
            filteredOperations.map((record) => (
              <OperationRow
                key={`${record.id}-${record.status}`}
                record={record}
                snapshot={snapshot}
                onOpenItem={onOpenItem}
              />
            ))
          )}
        </div>
      </BottomSheet>
    </section>
  )
}
